import { useState } from "react";
import {
  assignableBrands,
  brandByKey,
  CLIENT_TYPE_LABELS,
  clientTypesForBrand,
  type BrandKey,
  type ClientTypeValue,
} from "@school-sales-ai/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  TIMEZONE_OPTIONS,
  parseOrigins,
  slugify,
  withBrand,
  type ClientFormErrors,
  type ClientFormValues,
} from "@/components/client-form";
import type { Plan } from "@/lib/api";

/**
 * The client profile fields, as a controlled component.
 *
 * Shared by the Clients modal (`ClientFormModal`) and step 1 of the onboarding
 * wizard so the two cannot validate differently. Presentational on purpose: no
 * fetching, no mutation, no toast — the parent owns what saving means.
 *
 * The widget colour and Google Maps URL are NOT here. They live on `clients` too,
 * but they are written through `PUT /crm/settings`, which scopes itself with the
 * `X-Client-Id` header — from the Clients list that header points at whatever the
 * switcher last selected, so writing them from this form could hit another tenant.
 * The wizard renders them separately, where its tenant gate guarantees the scope.
 * See docs/plans/client-onboarding-wizard.md §3.3.
 */

type Props = {
  values: ClientFormValues;
  onChange: (values: ClientFormValues) => void;
  errors: ClientFormErrors;
  plans: Plan[];
  disabled?: boolean;
  /** Edit mode locks the slug and stops auto-suggesting it from the name. */
  isEdit: boolean;
  /**
   * Rendered with the location fields — the wizard puts the Google Maps URL here.
   *
   * A slot rather than a field of this form because it is written through
   * `PUT /crm/settings`, which the Clients modal must not call (§3.3): only a parent
   * that has pinned the tenant may render it.
   */
  locationExtras?: React.ReactNode;
  /** Rendered inside the "Widget web" section — the wizard puts the colour here. */
  widgetExtras?: React.ReactNode;
  /**
   * Whether `widgetExtras` currently holds a value. Only used to decide whether the
   * Widget web section starts open, and to describe it while collapsed — the parent
   * owns those values, so it is the only one that can say.
   */
  widgetExtrasConfigured?: boolean;
  /** How to describe `widgetExtras` in the collapsed summary, e.g. "color propio". */
  widgetExtrasLabel?: string;
};

const labelClass = "text-sm font-medium text-ink-700";
const errorClass = "text-xs text-red-500";
const hintClass = "text-xs text-ink-400";

export function ClientFormFields({
  values,
  onChange,
  errors,
  plans,
  disabled = false,
  isEdit,
  locationExtras,
  widgetExtras,
  widgetExtrasConfigured = false,
  widgetExtrasLabel = "color propio",
}: Props): React.JSX.Element {
  const originCount = parseOrigins(values.originsText).length;

  /**
   * Whether the Widget web section is expanded. UI state only: nothing about it is
   * persisted, and collapsing never clears a value.
   *
   * Derived from the data with an explicit override rather than initialised from it,
   * because the colour arrives asynchronously (the wizard loads it from
   * `GET /crm/settings` after mount) — a `useState` initialiser would have read it as
   * empty and left an already-configured client collapsed.
   */
  const [showWidgetOverride, setShowWidgetOverride] = useState<boolean | null>(null);
  const showWidget =
    showWidgetOverride ?? (originCount > 0 || widgetExtrasConfigured);

  const collapsedSummary = [
    originCount > 0
      ? `${originCount} ${originCount === 1 ? "dominio" : "dominios"}`
      : null,
    widgetExtrasConfigured ? widgetExtrasLabel : null,
  ]
    .filter(Boolean)
    .join(" y ");
  // Auto-suggest the slug until it diverges from what the name would produce —
  // that is what "the operator edited it" looks like without a separate touched
  // flag, and it survives this component remounting between wizard steps.
  function handleNameChange(name: string): void {
    const slugFollowsName = values.slug === slugify(values.name);
    onChange({
      ...values,
      name,
      slug: !isEdit && slugFollowsName ? slugify(name) : values.slug,
    });
  }

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Nombre</label>
        <Input
          value={values.name}
          onChange={(e) => handleNameChange(e.target.value)}
          disabled={disabled}
          placeholder="Instituto Ejemplo"
        />
        {errors.name && <span className={errorClass}>{errors.name}</span>}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Slug</label>
        <Input
          value={values.slug}
          onChange={(e) => onChange({ ...values, slug: e.target.value })}
          disabled={disabled || isEdit}
          placeholder="instituto-ejemplo"
        />
        {isEdit && <span className={hintClass}>El slug no se puede cambiar.</span>}
        {errors.slug && <span className={errorClass}>{errors.slug}</span>}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Marca</label>
        <Select
          value={values.brand}
          onValueChange={(v) => onChange(withBrand(values, v as BrandKey))}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* Only brands whose registry entry is complete. A brand still
                carrying placeholder values would point every deep link and auth
                redirect at a domain that does not resolve. */}
            {assignableBrands().map((b) => (
              <SelectItem key={b.key} value={b.key}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className={hintClass}>
          Decide el logotipo, el dominio de los correos y a dónde llevan los enlaces
          de invitación. No cambia nada de lo que el agente dice.
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Tipo de cliente</label>
        <Select
          // Keyed on the brand so switching brands remounts the list instead of
          // leaving Radix holding a value the new list does not contain.
          key={values.brand}
          value={values.clientType}
          onValueChange={(v) =>
            onChange({
              ...values,
              clientType: v as ClientTypeValue,
              mismatchedClientType: null,
            })
          }
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecciona el tipo" />
          </SelectTrigger>
          <SelectContent>
            {clientTypesForBrand(values.brand).map((t) => (
              <SelectItem key={t} value={t}>
                {CLIENT_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {values.mismatchedClientType && values.clientType === "" ? (
          <span className="text-xs text-amber-700">
            Estaba como «{CLIENT_TYPE_LABELS[values.mismatchedClientType]}», que no
            corresponde a {brandByKey(values.brand).name}. Elige uno de la lista.
          </span>
        ) : (
          <span className={hintClass}>
            Define la plantilla de base de conocimiento y el script sugerido.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Plan</label>
        <Select
          value={values.planId}
          onValueChange={(v) => onChange({ ...values, planId: v })}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecciona un plan" />
          </SelectTrigger>
          <SelectContent>
            {plans.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.planId && <span className={errorClass}>{errors.planId}</span>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className={labelClass}>Ciudad</label>
          <Input
            value={values.city}
            onChange={(e) => onChange({ ...values, city: e.target.value })}
            disabled={disabled}
            placeholder="Querétaro"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelClass}>Zona horaria</label>
          <Select
            value={values.timezone}
            onValueChange={(v) => onChange({ ...values, timezone: v })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.timezone && <span className={errorClass}>{errors.timezone}</span>}
        </div>
      </div>

      {locationExtras}

      {/* Last and optional: a client may sell entirely over WhatsApp and never embed
          the widget. Nothing above depends on these two, and leaving them empty is a
          valid configuration — not a half-finished one. */}
      <div className="mt-2 border-t border-ink-200 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink-900">
              Widget web <span className="font-normal text-ink-400">(opcional)</span>
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              Solo si el agente vivirá en el sitio del cliente. Si únicamente usará
              WhatsApp, déjalo apagado.
            </p>
          </div>
          <Switch
            checked={showWidget}
            onCheckedChange={setShowWidgetOverride}
            disabled={disabled}
            aria-label="Configurar el widget web"
          />
        </div>

        {showWidget ? (
          <>
            <div className="mt-3 flex flex-col gap-1.5">
              <label className={labelClass}>Orígenes del widget</label>
              <textarea
                value={values.originsText}
                onChange={(e) => onChange({ ...values, originsText: e.target.value })}
                disabled={disabled}
                rows={3}
                placeholder={"https://ejemplo.com\nhttps://www.ejemplo.com"}
                className="rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm text-ink-900 disabled:opacity-50 font-mono"
              />
              <span className={hintClass}>
                Un dominio por línea. El widget solo cargará en estos orígenes (CORS).
              </span>
              {errors.origins && <span className={errorClass}>{errors.origins}</span>}
            </div>

            {widgetExtras}
          </>
        ) : (
          // Collapsing only hides the fields — it does not clear them. Saying what is
          // still configured keeps the toggle from silently submitting hidden values.
          collapsedSummary && (
            <p className="mt-2 text-xs text-ink-500">
              Configurado: {collapsedSummary}. Enciende el interruptor para editarlo.
            </p>
          )
        )}
      </div>
    </>
  );
}
